import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Share, MoreVertical, PlusSquare, Smartphone, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "bkm_install_banner_dismissed";
const DISMISS_MS = 7 * 24 * 3600 * 1000;
const isPhone = () => /Android|iPhone|iPod|Mobile/i.test(navigator.userAgent) || window.matchMedia?.("(max-width: 767px)").matches;
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const Hint = ({ onClose, t }) => {
  const ios = isIOS();
  return createPortal(
    <div className={`rise fixed inset-x-3 z-[60] mx-auto flex max-w-sm items-center gap-3 rounded-2xl border border-amber-500/40 bg-[#121620] p-4 shadow-2xl ${ios ? "bottom-6" : "top-4"}`} data-testid="install-hint">
      <p className="flex flex-1 flex-wrap items-center gap-1.5 text-sm text-slate-100" data-testid={ios ? "install-hint-ios" : "install-hint-android"}>
        {ios
          ? <>{t("install.tap")} <Share className="h-5 w-5 text-sky-400" /> {t("install.then")} <PlusSquare className="h-4 w-4 text-amber-400" /> <b>{t("install.i3s")}</b></>
          : <>{t("install.tap")} <MoreVertical className="h-5 w-5 text-amber-400" /> {t("install.then")} <b>{t("install.a3s")}</b></>}
      </p>
      <button type="button" onClick={onClose} aria-label={t("install.later")} className="rounded-full p-1 text-slate-400" data-testid="install-hint-close"><X className="h-4 w-4" /></button>
      <span className={`absolute left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 border-amber-500/40 bg-[#121620] ${ios ? "-bottom-2 border-b border-r" : "-top-2 right-6 left-auto border-l border-t"}`} />
    </div>,
    document.body,
  );
};

export const InstallBanner = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [hidden, setHidden] = useState(() => Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0) < DISMISS_MS);
  if (!user || hidden || isStandalone() || !isPhone()) return null;
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setHidden(true); };
  return (
    <div className="rise mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3" data-testid="install-banner">
      <Smartphone className="h-5 w-5 shrink-0 text-amber-400" />
      <p className="min-w-0 flex-1 text-sm text-slate-200" data-testid="install-banner-text">{t("install.banner")}</p>
      <InstallApp compact onDone={dismiss} />
      <button type="button" onClick={dismiss} aria-label={t("install.later")}
        className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-colors hover:text-white" data-testid="install-banner-close">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const InstallApp = ({ compact = false, onDone }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(() => window.__bip || null);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); window.__bip = e; setPrompt(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const install = async () => {
    const p = prompt || window.__bip;
    if (!p) { setOpen(true); return; }
    p.prompt();
    const { outcome } = await p.userChoice;
    window.__bip = null;
    setPrompt(null);
    if (outcome === "accepted") { setInstalled(true); onDone?.(); }
  };

  return (
    <>
      <button
        type="button"
        onClick={install}
        className={compact
          ? "inline-flex h-9 items-center gap-1.5 rounded-full bg-amber-500 px-4 text-xs font-bold text-[#0B0D12] transition-colors hover:bg-amber-400"
          : "inline-flex h-12 items-center justify-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-6 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/20"}
        data-testid={compact ? "install-banner-btn" : "install-app-btn"}
      >
        <Download className="h-4 w-4" /> {compact ? t("install.short") : t("install.cta")}
      </button>
      {open && <Hint onClose={() => setOpen(false)} t={t} />}
    </>
  );
};
